import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Plus, Trophy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { tournamentAPI } from '../../tournaments/services/tournamentApi';
import { transformTournamentData } from '../../tournaments/utils/tournamentHelpers';
import TournamentCard from '../../tournaments/components/TournamentCard';
import EmptyState from '../../tournaments/components/EmptyState';
import ShareModal from '../../components/common/ShareModal';
import { btn } from '../../utils/buttonStyles';

export default function AcademyTournamentsTab({ academy, userRole }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  // A coach granted manage_tournaments (Settings → Staff → Access rights) can
  // create tournaments too, without being a full admin — matches what the
  // backend already enforces (tournamentsController.js's createTournament).
  const canCreateTournament = isAdmin || (academy?.myCapabilities || []).includes('manage_tournaments');

  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState({ isOpen: false, tournament: null });

  const fetchTournaments = useCallback(() => {
    if (!academy?.id) return;
    setLoading(true);
    tournamentAPI.getAll({ ownerType: 'academy', ownerId: academy.id, excludeCompleted: false })
      .then(response => setTournaments((response?.data || []).map(transformTournamentData)))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [academy?.id]);

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  // Prefer the slug for a nicer URL — academyAPI.getById accepts either.
  const academyKey = academy.slug || academy.id;

  const handleCreateTournament = () => {
    navigate('/create-tournament', {
      state: { ownerType: 'academy', ownerId: academy.id, ownerName: academy.name },
    });
  };

  const handleView = (tournament) => {
    navigate(`/academy/${academyKey}/tournament/${tournament.id}?tab=details`, {
      state: {
        tournament,
        fromAcademy: { id: academyKey, name: academy.name },
      },
    });
  };

  const handleShare = (tournament) => setShareModal({ isOpen: true, tournament });

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className={`text-xl font-bold ${theme.text}`}>Tournaments</h2>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''} hosted by {academy.name}
          </p>
        </div>
        {canCreateTournament && (
          <button
            onClick={handleCreateTournament}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Tournament
          </button>
        )}
      </div>

      {tournaments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tournaments.map(tournament => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              isMyTournament={isAdmin}
              onView={handleView}
              onShare={handleShare}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Trophy}
          title="No tournaments yet"
          description={
            canCreateTournament
              ? `Create the first tournament for ${academy.name}.`
              : `${academy.name} hasn't hosted any tournaments yet.`
          }
          buttonText={canCreateTournament ? 'Create Tournament' : undefined}
          onButtonClick={canCreateTournament ? handleCreateTournament : undefined}
        />
      )}

      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={() => setShareModal({ isOpen: false, tournament: null })}
        entity={{
          id: shareModal.tournament?.id,
          name: shareModal.tournament?.name,
          description: shareModal.tournament?.description,
        }}
        entityType="tournament"
        urlPattern="/tournament/{id}"
        currentUser={user}
      />
    </div>
  );
}
